import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { Loader2, TrendingUp, TrendingDown, AlertTriangle, Zap, Server, DollarSign } from 'lucide-react';

// --- FORMATTING LOGIC (PRESERVED 100%) ---
const formatCurrency = (val, country) => {
  if (!val && val !== 0) return '—';
  
  const mappings = {
    'India': { currency: 'INR', locale: 'en-IN' },
    'Singapore': { currency: 'SGD', locale: 'en-SG' },
    'Australia': { currency: 'AUD', locale: 'en-AU' },
    'New Zealand': { currency: 'NZD', locale: 'en-NZ' },
    'Japan': { currency: 'JPY', locale: 'ja-JP' },
    'South Korea': { currency: 'KRW', locale: 'ko-KR' },
    'Hong Kong': { currency: 'HKD', locale: 'en-HK' },
    'United States': { currency: 'USD', locale: 'en-US' },
    'Canada': { currency: 'CAD', locale: 'en-CA' },
    'United Kingdom': { currency: 'GBP', locale: 'en-GB' },
    'Sweden': { currency: 'SEK', locale: 'sv-SE' },
    'Switzerland': { currency: 'CHF', locale: 'de-CH' },
    'United Arab Emirates': { currency: 'AED', locale: 'en-AE' },
    'Saudi Arabia': { currency: 'SAR', locale: 'en-SA' },
    'South Africa': { currency: 'ZAR', locale: 'en-ZA' }
  };

  const euCountries = [
    'Germany', 'France', 'Netherlands', 'Ireland', 
    'Austria', 'Belgium', 'Cyprus', 'Estonia', 'Finland', 'Greece',
    'Italy', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'Portugal',
    'Slovakia', 'Slovenia', 'Spain'
  ];

  let currency = 'USD'; 
  let locale = 'en-US';

  if (mappings[country]) {
    currency = mappings[country].currency;
    locale = mappings[country].locale;
  } else if (euCountries.includes(country)) {
    currency = 'EUR';
    locale = 'en-IE';
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    notation: "compact",
    maximumFractionDigits: 1
  }).format(val);
};

// --- ANIMATION VARIANTS (THE UX POLISH) ---
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { 
    opacity: 1, 
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 24 }
  }
};

export default function RoiDashboard() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [data, setData] = useState([]);
  const [cacheHits, setCacheHits] = useState(0);
  const [isSynthesizing, setIsSynthesizing] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    if (!user || !user.org_id) return;

    try {
      setLoading(true);
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      const [wfRes, fwRes] = await Promise.all([
        axios.get(`${baseURL}/api/sprint/workflow-map/${user.org_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${baseURL}/api/firewall/logs`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { cacheHits: 0 } }))
      ]);

      setData(wfRes.data || []);
      setCacheHits(fwRes.data?.cacheHits || 0);

    } catch (error) {
      console.error("Failed to load dashboard data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.org_id, token]);

  const handleSynthesize = async () => {
    setIsSynthesizing(true);
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      await axios.post(`${baseURL}/api/sprint/synthesize-roi`, 
        { orgId: user.org_id }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchData(); 
    } catch (error) {
      console.error("Synthesis failed", error);
      alert("Failed to synthesize data. Please try again.");
    } finally {
      setIsSynthesizing(false);
    }
  };

  const getDotColor = (impact, effort) => {
    if (impact >= 5 && effort < 5) return '#10b981'; 
    if (impact >= 5 && effort >= 5) return '#3b82f6'; 
    if (impact < 5 && effort >= 5) return '#ef4444'; 
    return '#94a3b8'; 
  };

  // --- FINANCIAL MATH ENGINE ---
  const needsSynthesis = data.some(wf => !wf.impact_score);
  const totalPotentialSavings = data.filter(wf => wf.impact_score >= 6).reduce((acc, curr) => acc + ((Number(curr.fte_cost) || 0) * 0.4), 0); 
  const firewallCacheSavings = cacheHits * 0.02;
  const baseComputeCost = totalPotentialSavings > 0 ? (totalPotentialSavings * 0.05) : 0; 
  const actualApiCosts = Math.max(0, baseComputeCost - firewallCacheSavings);
  const netRoi = (totalPotentialSavings + firewallCacheSavings) - actualApiCosts;

  // --- PREMIUM LOADING STATE ---
  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] w-full">
      <motion.div 
        animate={{ scale: [1, 1.1, 1], rotate: [0, 90, 180, 270, 360] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        className="w-16 h-16 bg-gradient-to-tr from-indigo-600 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/30 mb-6"
      >
        <Zap className="w-8 h-8 text-white" />
      </motion.div>
      <h3 className="text-xl font-bold text-slate-800 tracking-tight">Compiling Financial ROI...</h3>
      <p className="text-slate-500 text-sm mt-2">Aggregating telemetry from Zero-Trust Gateway</p>
    </div>
  );

  return (
    <motion.div 
      className="max-w-7xl mx-auto p-6 pb-24"
      variants={containerVariants}
      initial="hidden"
      animate="show"
    >
      
      {/* HEADER */}
      <motion.div variants={itemVariants} className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 border-b border-slate-200 pb-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Enterprise ROI & Impact Matrix</h1>
          <p className="text-slate-500 mt-2 font-medium">Financial value delivery and deployment prioritization plotted by FCAIO.</p>
        </div>
        
        {needsSynthesis && (
          <motion.button 
            whileHover={{ scale: 1.02, boxShadow: "0 10px 25px -5px rgba(79, 70, 229, 0.4)" }}
            whileTap={{ scale: 0.98 }}
            onClick={handleSynthesize} 
            disabled={isSynthesizing}
            className="mt-4 md:mt-0 flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-xl shadow-md font-bold transition-all"
          >
            {isSynthesizing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
            {isSynthesizing ? 'Synthesizing Data...' : 'Run ROI Synthesis'}
          </motion.button>
        )}
      </motion.div>

      {/* EXECUTIVE FINANCIAL ROW */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        
        {/* Gross Savings */}
        <motion.div variants={itemVariants} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm relative overflow-hidden group hover:border-blue-300 transition-colors duration-300">
          <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity duration-500 group-hover:scale-110 transform"><Zap className="w-24 h-24 text-blue-600" /></div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Projected FTE Savings</p>
          <h2 className="text-4xl font-black text-slate-900 mb-2 tracking-tight">{formatCurrency(totalPotentialSavings, user?.country)}</h2>
          <div className="flex items-center gap-1 text-sm font-bold text-emerald-600 bg-emerald-50 w-fit px-2.5 py-1 rounded-md">
            <TrendingUp className="w-4 h-4" /> From {data.filter(wf => wf.impact_score >= 6).length} viable workflows
          </div>
        </motion.div>

        {/* Semantic Cache Savings */}
        <motion.div variants={itemVariants} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm relative overflow-hidden group hover:border-indigo-300 transition-colors duration-300">
          <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity duration-500 group-hover:scale-110 transform"><Server className="w-24 h-24 text-indigo-600" /></div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">FCAIO Cache Savings</p>
          <h2 className="text-4xl font-black text-slate-900 mb-2 tracking-tight">{formatCurrency(firewallCacheSavings, user?.country)}</h2>
          <div className="flex items-center gap-1 text-sm font-bold text-emerald-600 bg-emerald-50 w-fit px-2.5 py-1 rounded-md">
            <TrendingDown className="w-4 h-4" /> Avoided API token costs ({cacheHits} hits)
          </div>
        </motion.div>

        {/* Net Value */}
        <motion.div variants={itemVariants} className="bg-gradient-to-br from-emerald-600 to-emerald-800 rounded-2xl p-6 border border-emerald-500 shadow-xl shadow-emerald-600/20 relative overflow-hidden text-white hover:-translate-y-1 transition-transform duration-300">
          <div className="absolute top-0 right-0 p-4 opacity-10 scale-110"><DollarSign className="w-32 h-32" /></div>
          <p className="text-xs font-bold text-emerald-200 uppercase tracking-wider mb-2">Net Value Delivered</p>
          <h2 className="text-5xl font-black text-white mb-2 tracking-tighter drop-shadow-md">{formatCurrency(netRoi, user?.country)}</h2>
          <div className="mt-auto bg-emerald-900/40 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-emerald-500/30 w-fit">
            <p className="text-xs font-semibold text-emerald-100">After Compute Costs: {formatCurrency(actualApiCosts, user?.country)}</p>
          </div>
        </motion.div>

      </div>

      {/* ORIGINAL MATRIX & ACTION LIST */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Scatter Plot */}
        <motion.div variants={itemVariants} className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200 p-6 h-[600px] flex flex-col relative overflow-hidden group">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-indigo-600" /> Impact vs. Effort Prioritization</h2>
          </div>
          
          <div className="absolute top-16 left-24 text-emerald-600/10 font-black text-3xl uppercase tracking-widest pointer-events-none z-0">Quick Wins</div>
          <div className="absolute top-16 right-16 text-blue-600/10 font-black text-3xl uppercase tracking-widest pointer-events-none z-0">Strategic Bets</div>
          <div className="absolute bottom-16 left-24 text-slate-400/10 font-black text-3xl uppercase tracking-widest pointer-events-none z-0">Fill-Ins</div>
          <div className="absolute bottom-16 right-16 text-red-500/10 font-black text-3xl uppercase tracking-widest pointer-events-none z-0">Money Pits</div>

          <div className="relative z-10 w-full h-96 min-h-[500px]">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              
                <XAxis type="number" dataKey="effort_score" name="Implementation Effort" domain={[0, 10]} ticks={[0,2,4,6,8,10]} label={{ value: "← Lower Effort | Higher Effort →", position: "bottom", offset: -10, fill: '#64748b', fontSize: 12, fontWeight: 600 }} axisLine={false} tickLine={false} />
                <YAxis type="number" dataKey="impact_score" name="Business Impact" domain={[0, 10]} ticks={[0,2,4,6,8,10]} label={{ value: "Business Impact (ROI)", angle: -90, position: "insideLeft", offset: 10, fill: '#64748b', fontSize: 12, fontWeight: 600 }} axisLine={false} tickLine={false} />
              
                <ZAxis type="number" dataKey="impact_score" range={[400, 400]} />
              
                <Tooltip cursor={{ strokeDasharray: '3 3', stroke: '#cbd5e1' }} content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const wf = payload[0].payload;
                    const estSavings = wf.impact_score >= 6 ? (Number(wf.fte_cost) * 0.4) : 0; 

                    return (
                      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-slate-900/95 backdrop-blur-md text-white p-4 rounded-xl shadow-2xl max-w-xs border border-slate-700/50">
                        <p className="font-bold text-lg border-b border-slate-700 pb-2 mb-2 leading-tight">{wf.process_name}</p>
                        <p className="text-sm"><span className="text-slate-400 font-medium">Strategy:</span> {wf.ai_suitability}</p>
                      
                        {estSavings > 0 && (
                          <div className="mt-2 text-emerald-400 text-sm font-bold bg-emerald-500/10 border border-emerald-500/20 px-2 py-1.5 rounded-lg inline-block">
                            Est. Value: {formatCurrency(estSavings, user?.country)}/yr
                          </div>
                        )}

                        <div className="flex gap-4 mt-3 mb-2 bg-slate-800/50 p-2.5 rounded-lg">
                          <div><span className="text-slate-400 text-xs block uppercase tracking-wider mb-0.5">Impact</span><span className="font-bold text-emerald-400 text-lg">{wf.impact_score}<span className="text-xs text-slate-500">/10</span></span></div>
                          <div><span className="text-slate-400 text-xs block uppercase tracking-wider mb-0.5">Effort</span><span className="font-bold text-amber-400 text-lg">{wf.effort_score}<span className="text-xs text-slate-500">/10</span></span></div>
                        </div>
                        <p className="text-xs text-slate-400 italic line-clamp-3">"{wf.synthesis_rationale}"</p>
                      </motion.div>
                    );
                  }
                  return null;
                }} />
              
                <ReferenceLine x={5} stroke="#cbd5e1" strokeWidth={2} strokeDasharray="4 4" />
                <ReferenceLine y={5} stroke="#cbd5e1" strokeWidth={2} strokeDasharray="4 4" />
              
                <Scatter name="Workflows" data={data.filter(w => w.impact_score)}>
                  {data.filter(w => w.impact_score).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getDotColor(entry.impact_score, entry.effort_score)} className="transition-all duration-300 hover:opacity-80 drop-shadow-md" />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
       
        {/* Action List */}
        <motion.div variants={itemVariants} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 overflow-y-auto h-[600px] custom-scrollbar">
          <h2 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500"/> Priority Action List</h2>
          
          <div className="space-y-4">
            {data.filter(w => w.impact_score).sort((a, b) => b.impact_score - a.impact_score).map(wf => {
              const wfSavings = wf.impact_score >= 6 ? (Number(wf.fte_cost) * 0.4) : 0;

              return (
                <motion.div 
                  whileHover={{ scale: 1.01 }}
                  key={wf.id} 
                  className="p-4 border border-slate-100 bg-slate-50 hover:bg-white hover:shadow-md hover:border-slate-300 transition-all rounded-xl cursor-pointer"
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-slate-900 text-sm leading-tight pr-2">{wf.process_name}</h3>
                    <span className={`px-2 py-1 rounded-md text-[10px] uppercase tracking-widest font-black whitespace-nowrap ${wf.impact_score >= 5 && wf.effort_score < 5 ? 'bg-emerald-100 text-emerald-700' : wf.impact_score >= 5 ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-600'}`}>
                      {wf.impact_score >= 5 && wf.effort_score < 5 ? 'Quick Win' : wf.impact_score >= 5 ? 'Strategic' : 'Low Priority'}
                    </span>
                  </div>
                  
                  {wfSavings > 0 && (
                    <div className="mb-2.5">
                      <span className="text-emerald-700 font-bold text-xs bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-lg inline-flex items-center gap-1.5 shadow-sm">
                        <DollarSign className="w-3 h-3"/> Save {formatCurrency(wfSavings, user?.country)}/yr
                      </span>
                    </div>
                  )}

                  <p className="text-xs font-bold text-indigo-600 mb-1.5">{wf.ai_suitability}</p>
                  <p className="text-xs text-slate-500 italic line-clamp-2">"{wf.synthesis_rationale}"</p>
                </motion.div>
              );
            })}
            
            {!data.some(w => w.impact_score) && !isSynthesizing && (
              <div className="text-center p-8 text-slate-500 border-2 border-dashed border-slate-200 rounded-2xl mt-4 bg-slate-50/50">
                <Zap className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                <p className="text-sm font-medium">Click "Run ROI Synthesis" to generate actionable AI insights and financial data.</p>
              </div>
            )}
          </div>
        </motion.div>

      </div>
    </motion.div>
  );
}